import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Cake as CakeIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import {
  getLeads,
  addLead,
  updateLead,
  LeadStatus,
  LeadStatusLabels,
} from '../Services/leadsService';
import { useAuth } from '../contexts/JWTAuthContext';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3, 0),
}));

const LEAD_STATUS_COLORS = { 0: 'success', 1: 'error', 2: 'warning' };

const DetailField = ({ label, value, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
    {icon && <Box sx={{ color: 'text.secondary', mt: 0.25 }}>{icon}</Box>}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const Leads = () => {
  const { user } = useAuth();
  const salesId = user?.sub ?? user?.id ?? user?.Id ?? '';

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', phoneNumber: '', note: '', leadStatus: null });
  const [addForm, setAddForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    note: '',
    leadStatus: '',
    salesId: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getLeads({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        search: filters.search,
      });
      const leadsData = Array.isArray(result.data) ? result.data : [];
      setLeads(leadsData);
      setPagination(prev => ({
        ...prev,
        totalCount: result.totalCount ?? 0,
        totalPages: result.pagesCount ?? Math.ceil((result.totalCount ?? 0) / prev.pageSize),
      }));
    } catch (error) {
      console.error('Error fetching leads:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to load leads',
        severity: 'error',
      });
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: newPage + 1,
    }));
  };

  const handlePageSizeChange = (event) => {
    setPagination(prev => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 1,
    }));
  };

  const handleSearch = () => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 1,
    }));
    fetchLeads();
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
    });
    setPagination(prev => ({
      ...prev,
      pageIndex: 1,
    }));
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditDialogOpen(false);
    setSelectedLead(null);
    setEditForm({ name: '', email: '', phoneNumber: '', address: '', note: '', leadStatus: null });
  };

  const handleOpenAddDialog = () => {
    setAddForm({
      name: '',
      email: '',
      phoneNumber: '',
      address: '',
      note: '',
      leadStatus: '',
      salesId: salesId,
    });
    setAddDialogOpen(true);
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitAddLead = async () => {
    if (!addForm.name?.trim() || !addForm.email?.trim()) {
      setSnackbar({ open: true, message: 'Name and email are required', severity: 'error' });
      return;
    }
    if (!salesId) {
      setSnackbar({ open: true, message: 'Sales user not found', severity: 'error' });
      return;
    }
    try {
      await addLead({
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        phoneNumber: addForm.phoneNumber || undefined,
        address: addForm.address || undefined,
        note: addForm.note || undefined,
        leadStatus: addForm.leadStatus !== '' ? Number(addForm.leadStatus) : undefined,
        salesId,
      });
      setSnackbar({ open: true, message: 'Lead added successfully', severity: 'success' });
      setAddDialogOpen(false);
      fetchLeads();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || error?.errors || 'Failed to add lead',
        severity: 'error',
      });
    }
  };

  const handleOpenEdit = () => {
    if (!selectedLead) return;
    const dob = selectedLead.dateOfBirth ?? selectedLead.DateOfBirth ?? '';
    setEditForm({
      name: selectedLead.name ?? selectedLead.Name ?? '',
      email: selectedLead.email ?? selectedLead.Email ?? '',
      phoneNumber: selectedLead.phoneNumber ?? selectedLead.PhoneNumber ?? '',
      address: selectedLead.address ?? selectedLead.Address ?? '',
      note: selectedLead.note ?? selectedLead.Note ?? '',
      leadStatus: selectedLead.leadStatus ?? selectedLead.LeadStatus ?? null,
    });
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: name === 'leadStatus' ? (value === '' ? null : Number(value)) : value,
    }));
  };

  const handleSubmitUpdateLead = async () => {
    if (!selectedLead) return;
    const leadUserId = selectedLead.id ?? selectedLead.Id;
    try {
      await updateLead(leadUserId, {
        name: editForm.name || undefined,
        email: editForm.email || undefined,
        phoneNumber: editForm.phoneNumber || undefined,
        address: editForm.address || undefined,
        note: editForm.note,
        leadStatus: editForm.leadStatus,
      });
      setSnackbar({ open: true, message: 'Lead updated successfully', severity: 'success' });
      setEditDialogOpen(false);
      setDialogOpen(false);
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || error?.errors || 'Failed to update lead',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Helper function to render lead field value
  const renderField = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return new Date(value).toLocaleString();
    return value.toString();
  };

  // Table columns: include  name, email, phoneNumber, note, leadStatus, createdAt
const getTableHeaders = () => ['name', 'email', 'phoneNumber', 'address'];    

  const renderLeadStatus = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const n = Number(value);
    return LeadStatusLabels[n] ?? String(value);
  };

  const getLeadValue = (lead, header) => {
    const v = lead[header];
    if (v !== undefined && v !== null) return v;
    const pascal = header.charAt(0).toUpperCase() + header.slice(1);
    return lead[pascal];
  };

  return (
    <Container maxWidth="xl">
      <StyledPaper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            My Leads
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mr: 1 }}
            >
              Add Lead
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchLeads}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Search & Filters</Typography>
              <Button
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                sx={{ ml: 'auto' }}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </Box>
            
            {showFilters && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Search"
                      name="search"
                      value={filters.search}
                      onChange={handleFilterChange}
                      placeholder="Search leads..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                  >
                    Reset Filters
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Data Grid */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Data Grid Table */}
                {leads.length > 0 ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          {getTableHeaders().map((header, index) => (
                            <th 
                              key={index}
                              style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}
                            >
                              {header.charAt(0).toUpperCase() + header.slice(1).replace(/([A-Z])/g, ' $1')}
                            </th>
                          ))}
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead, index) => (
                          <tr key={lead.Id || lead.id || index} style={{ borderBottom: '1px solid #eee' }}>
                            {getTableHeaders().map((header, colIndex) => {
                              const val = getLeadValue(lead, header);
                              return (
                                <td key={colIndex} style={{ padding: '12px' }}>
                                  {header === 'leadStatus' ? (
                                    renderLeadStatus(val)
                                  ) : header.toLowerCase().includes('date') || header.toLowerCase().includes('at') ? (
                                    val ? new Date(val).toLocaleDateString() : 'N/A'
                                  ) : (
                                    renderField(val)
                                  )}
                                </td>
                              );
                            })}
                            <td style={{ padding: '12px' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewLead(lead)}
                                title="View Details"
                                color="primary"
                              >
                                <ViewIcon />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No leads found
                    </Typography>
                  </Box>
                )}

                {/* Pagination */}
                {leads.length > 0 && (
                  <TablePagination
                    component="div"
                    count={pagination.totalCount}
                    page={pagination.pageIndex - 1}
                    onPageChange={handlePageChange}
                    rowsPerPage={pagination.pageSize}
                    onRowsPerPageChange={handlePageSizeChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </StyledPaper>

      {/* Lead Details Dialog */}
      <Dialog open={dialogOpen && !editDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {selectedLead && (() => {
          const status = selectedLead.leadStatus ?? selectedLead.LeadStatus;
          const statusLabel = status !== undefined && status !== null ? (LeadStatusLabels[status] ?? '—') : '—';
          const statusColor = status !== undefined && status !== null ? (LEAD_STATUS_COLORS[status] ?? 'default') : 'default';
          return (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" component="span">Lead Details</Typography>
                </Box>
                <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
              </DialogTitle>

              <Divider />

              <DialogContent sx={{ pt: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {selectedLead.name ?? selectedLead.Name ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLead.email ?? selectedLead.Email ?? '—'}
                    </Typography>
                  </Box>
                  <Chip label={statusLabel} color={statusColor} size="medium" sx={{ fontWeight: 500, fontSize: '0.875rem' }} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Contact Information */}
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <DetailField label="Phone Number" value={selectedLead.phoneNumber ?? selectedLead.PhoneNumber} icon={<PhoneIcon fontSize="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DetailField label="Email" value={selectedLead.email ?? selectedLead.Email} icon={<EmailIcon fontSize="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DetailField label="Address" value={selectedLead.address ?? selectedLead.Address} icon={<HomeIcon fontSize="small" />} />
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                {/* Personal Information */}
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {/* Lead Information */}
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Lead Information
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Note" value={selectedLead.note ?? selectedLead.Note} icon={<NotesIcon fontSize="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Role" value={selectedLead.role ?? selectedLead.Role} icon={<PersonIcon fontSize="small" />} />
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />
              </DialogContent>

              <Divider />

              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button startIcon={<EditIcon />} onClick={handleOpenEdit} variant="outlined">Edit</Button>
                <Button onClick={handleCloseDialog} color="inherit">Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EditIcon color="primary" />
          <Typography variant="h6" component="span">Edit Lead</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Name" name="name" value={editForm.name} onChange={handleEditFormChange} />
            </Grid>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Email" name="email" type="email" value={editForm.email} onChange={handleEditFormChange} />
              </Grid>
              <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Phone" name="phoneNumber" value={editForm.phoneNumber} onChange={handleEditFormChange} />
            </Grid>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Address" name="address" value={editForm.address} onChange={handleEditFormChange} />
            </Grid>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Note" name="note" value={editForm.note} onChange={handleEditFormChange} multiline rows={3} />
            </Grid>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Lead Status</InputLabel>
                <Select name="leadStatus" value={editForm.leadStatus ?? ''} onChange={handleEditFormChange} label="Lead Status">
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value={LeadStatus.Interested}>{LeadStatusLabels[LeadStatus.Interested]}</MenuItem>
                  <MenuItem value={LeadStatus.NotInterested}>{LeadStatusLabels[LeadStatus.NotInterested]}</MenuItem>
                  <MenuItem value={LeadStatus.CallHimBack}>{LeadStatusLabels[LeadStatus.CallHimBack]}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleSubmitUpdateLead}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Lead</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Name" name="name" value={addForm.name} onChange={handleAddFormChange} required />
            </Grid>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Email" name="email" type="email" value={addForm.email} onChange={handleAddFormChange} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone" name="phoneNumber" value={addForm.phoneNumber} onChange={handleAddFormChange} />
            </Grid>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <TextField fullWidth label="Address" name="address" value={addForm.address} onChange={handleAddFormChange} />
            </Grid>
            <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Lead Status</InputLabel>
                <Select name="leadStatus" value={addForm.leadStatus} onChange={handleAddFormChange} label="Lead Status">
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value={LeadStatus.Interested}>{LeadStatusLabels[LeadStatus.Interested]}</MenuItem>
                  <MenuItem value={LeadStatus.NotInterested}>{LeadStatusLabels[LeadStatus.NotInterested]}</MenuItem>
                  <MenuItem value={LeadStatus.CallHimBack}>{LeadStatusLabels[LeadStatus.CallHimBack]}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Note" name="note" value={addForm.note} onChange={handleAddFormChange} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitAddLead}>Add Lead</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Leads;

